// Copyright (c) 2026 Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, View as RNView } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { Text, View } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { useUser } from '@/app/user-context';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { sendAndAwaitAck } from '@/services/wsAck';
import { formatRespondedAt, formatTeeTime, rsvpStatusLabel } from '@/features/round-scheduling/format';
import type { Responder, RsvpStatus, UpcomingRound } from '@/features/round-scheduling/types';
import appColors from '@/theme/colors';
import { t } from '@/i18n';

type RoundInfo = {
  teeTimeUtc?: string;
  courseName?: string;
};

const getString = (record: Record<string, unknown>, ...keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return undefined;
};

export default function RsvpScreen() {
  const { roundId: roundIdParam } = useLocalSearchParams<{ roundId?: string }>();
  const roundId = Array.isArray(roundIdParam) ? roundIdParam[0] : roundIdParam;
  const { user } = useUser();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const [playerId, setPlayerId] = useState<string | null>(null);
  const [roundInfo, setRoundInfo] = useState<RoundInfo | null>(null);
  const [responders, setResponders] = useState<Responder[]>([]);
  const [myStatus, setMyStatus] = useState<Responder['rsvpStatus'] | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const loadData = useCallback(async () => {
    if (!roundId || !user?.id || !user.token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Resolve player id (needed for the upcoming query and to send an RSVP).
      let resolvedPlayerId = playerId;
      if (!resolvedPlayerId) {
        const playerResponse = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (playerResponse.ok) {
          const payload = (await playerResponse.json()) as { id?: string };
          resolvedPlayerId = payload.id ?? null;
          setPlayerId(resolvedPlayerId);
        }
      }

      // Round detail carries the tee time + course; the RSVP roster comes from the upcoming
      // feed (which the wire contract guarantees returns responders), filtered by roundId.
      const [detailResult, upcomingResult] = await Promise.allSettled([
        fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        }),
        resolvedPlayerId
          ? fetch(`${apiBaseUrl}/api/rounds/upcoming?playerId=${encodeURIComponent(resolvedPlayerId)}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          : Promise.reject(new Error('no player id')),
      ]);

      let info: RoundInfo | null = null;
      if (detailResult.status === 'fulfilled' && detailResult.value.ok) {
        const detail = (await detailResult.value.json()) as Record<string, unknown>;
        info = {
          teeTimeUtc: getString(detail, 'teeTimeUtc', 'TeeTimeUtc', 'playDate', 'PlayDate'),
          courseName: getString(detail, 'courseName', 'CourseName'),
        };
      }

      if (upcomingResult.status === 'fulfilled' && upcomingResult.value.ok) {
        const list = (await upcomingResult.value.json()) as unknown;
        const entries = Array.isArray(list) ? list : [];
        const match = entries
          .map((entry) => (entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : null))
          .find((entry) => entry && (entry.roundId ?? entry.RoundId) === roundId);
        if (match) {
          const upcoming = match as unknown as UpcomingRound;
          info = {
            teeTimeUtc: info?.teeTimeUtc ?? upcoming.teeTimeUtc,
            courseName: info?.courseName ?? upcoming.courseName,
          };
          setResponders(Array.isArray(upcoming.responders) ? upcoming.responders : []);
          setMyStatus(upcoming.myRsvpStatus);
        }
      }

      setRoundInfo(info);
      if (!info) setError(t('rsvp.loadError'));
    } catch {
      setError(t('rsvp.loadError'));
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, playerId, roundId, user?.id, user?.token]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Live roster: apply every round.rsvpUpdated for this round to the local list.
  useEffect(() => {
    if (!roundId || !user?.token) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user.token);
    const unsubscribe = socket.onMessage((message) => {
      const frame = message as Record<string, unknown>;
      if (frame.type !== 'round.rsvpUpdated') return;
      if ((frame.roundId ?? frame.RoundId) !== roundId) return;
      const updatedPlayerId = (frame.playerId ?? frame.PlayerId) as string | undefined;
      const status = (frame.rsvpStatus ?? frame.RsvpStatus) as Responder['rsvpStatus'] | undefined;
      const respondedAtUtc = (frame.respondedAtUtc ?? frame.RespondedAtUtc) as string | undefined;
      if (!updatedPlayerId || !status) return;
      setResponders((prev) => {
        const existing = prev.find((r) => r.playerId === updatedPlayerId);
        if (existing) {
          return prev.map((r) =>
            r.playerId === updatedPlayerId ? { ...r, rsvpStatus: status, respondedAtUtc } : r
          );
        }
        return [...prev, { playerId: updatedPlayerId, rsvpStatus: status, respondedAtUtc }];
      });
      if (updatedPlayerId === playerId) setMyStatus(status);
    });
    return () => {
      unsubscribe();
    };
  }, [apiBaseUrl, playerId, roundId, user?.token]);

  const handleRsvp = useCallback(
    async (rsvpStatus: RsvpStatus) => {
      if (!roundId || !playerId || !user?.token || sending) return;
      setSending(true);
      setError(null);
      try {
        const socket = getSharedBffWebSocket(apiBaseUrl);
        socket.setAuthToken(user.token);
        const result = await sendAndAwaitAck(
          socket,
          { type: 'round.rsvp', roundId, playerId, rsvpStatus },
          ['round.rsvpUpdated', 'round.rsvp.rejected']
        );
        if (result.type === 'round.rsvp.rejected') {
          const message = typeof result.message === 'string' ? result.message : '';
          setError(message || t('rsvp.sendError'));
        } else {
          // The onMessage listener also applies this, but update immediately for responsiveness.
          setMyStatus(rsvpStatus);
        }
      } catch {
        setError(t('rsvp.sendError'));
      } finally {
        setSending(false);
      }
    },
    [apiBaseUrl, playerId, roundId, sending, user?.token]
  );

  if (loading) {
    return (
      <RNView style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#A1D493" />
      </RNView>
    );
  }

  return (
    <RNView style={styles.pageBackground}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.container}>
          <View style={styles.hero}>
            <Text style={styles.title}>{t('rsvp.heading')}</Text>
            <Text style={styles.subtitle}>{roundInfo?.courseName ?? '-'}</Text>
            <Text style={styles.teeTime}>
              {t('rsvp.teeTime')}: {formatTeeTime(roundInfo?.teeTimeUtc) || '-'}
            </Text>
          </View>

          <View style={styles.card}>
            <Text style={styles.label}>{t('rsvp.myStatusLabel')}</Text>
            <Text style={styles.myStatusValue}>{rsvpStatusLabel(myStatus)}</Text>
            <View style={styles.buttonsRow}>
              <GoffyButton
                label={t('rsvp.accept')}
                onPress={() => void handleRsvp('accepted')}
                disabled={sending}
                tone="accent"
                style={styles.actionButton}
                testID="rsvp-accept"
              />
              <GoffyButton
                label={t('rsvp.maybe')}
                onPress={() => void handleRsvp('tentative')}
                disabled={sending}
                tone="dark"
                style={styles.actionButton}
                testID="rsvp-maybe"
              />
              <GoffyButton
                label={t('rsvp.decline')}
                onPress={() => void handleRsvp('declined')}
                disabled={sending}
                tone="dark"
                style={styles.actionButton}
                testID="rsvp-decline"
              />
            </View>
            {sending ? <Text style={styles.sending}>{t('rsvp.sending')}</Text> : null}
            {error ? <Text style={styles.error}>{error}</Text> : null}
          </View>

          <View style={styles.card}>
            <Text style={styles.label}>{t('rsvp.whoIsComing')}</Text>
            {responders.length === 0 ? (
              <Text style={styles.empty}>{t('rsvp.noResponses')}</Text>
            ) : (
              responders.map((responder) => (
                <View key={responder.playerId} style={styles.rosterRow}>
                  <View style={styles.rosterText}>
                    <Text style={styles.rosterName}>{responder.playerName ?? responder.playerId}</Text>
                    {responder.respondedAtUtc ? (
                      <Text style={styles.rosterMeta}>
                        {t('rsvp.respondedAt', { time: formatRespondedAt(responder.respondedAtUtc) })}
                      </Text>
                    ) : null}
                  </View>
                  <Text style={styles.rosterStatus}>{rsvpStatusLabel(responder.rsvpStatus)}</Text>
                </View>
              ))
            )}
          </View>
        </View>
      </ScrollView>
    </RNView>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: appColors.background.app,
  },
  scrollContent: {
    padding: 16,
  },
  container: {
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
    gap: 14,
  },
  hero: {
    gap: 4,
  },
  title: {
    color: appColors.text.heading,
    fontSize: 24,
    fontWeight: '800',
  },
  subtitle: {
    color: appColors.text.secondary,
    fontSize: 16,
  },
  teeTime: {
    color: '#A1D493',
    fontSize: 14,
    marginTop: 2,
  },
  card: {
    borderRadius: 16,
    padding: 18,
    backgroundColor: '#07200D',
    borderWidth: 1,
    borderColor: '#1A3A20',
    gap: 10,
  },
  label: {
    color: appColors.text.primary,
    fontWeight: '700',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontSize: 13,
  },
  myStatusValue: {
    color: '#A1D493',
    fontSize: 16,
    fontWeight: '700',
  },
  buttonsRow: {
    flexDirection: 'row',
    columnGap: 8,
  },
  actionButton: {
    flex: 1,
  },
  sending: {
    color: '#7FA978',
    fontSize: 12,
  },
  error: {
    color: '#ff6666',
  },
  empty: {
    color: '#C8C8B0',
    fontSize: 13,
  },
  rosterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: 10,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: '#1A3A20',
  },
  rosterText: {
    flex: 1,
    minWidth: 0,
  },
  rosterName: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  rosterMeta: {
    color: '#7FA978',
    fontSize: 11,
    marginTop: 2,
  },
  rosterStatus: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '600',
  },
});
